/* =====================================================================================
   Structured search: many criteria, ranked best fit first.

   THE PROBLEM THIS SOLVES. Forum already had text search, which answers "where is the thing I
   can name". It could not answer the question that actually gets asked, which is shaped like:

     the hundred best mid-size kidney units, with a colocated HLA laboratory, in the United
     States, where sending out is a nuisance and building in-house makes no sense

   Every filter in the product until now was a hard AND: pick a country, pick an organ, get a
   list. That cannot express "mid-size", which is a preference with a shape, and it cannot rank,
   so the answer to a good question was five hundred rows in alphabetical order.

   TWO KINDS OF CRITERION, and the distinction is the whole design.

     MUST   excludes. Not in the United States, not in the list. No argument.
     PREFER ranks. Nothing is excluded for failing it; a centre that satisfies it sorts higher.

   That mapping is what makes the example above expressible: country and colocated laboratory are
   MUST, mid-size and send-out volume are PREFER. Ask for all of it as MUST and you get four
   centres and a false impression that there are only four worth calling.

   NUMERIC PREFERENCES ARE GRADED, NOT BINARY. "Mid-size, 50 to 200" scored as a yes-or-no makes
   a 201-case unit worth exactly as much as a 5,000-case one, which is wrong and would put the
   biggest centres in the country at the bottom next to the smallest. Inside the band scores 1;
   outside it decays with distance, so 210 lands just below the band and 4,000 lands far away.

   EVERY RESULT CARRIES ITS REASONS. A ranked list nobody can interrogate is a ranked list nobody
   trusts, and the first question anyone asks of one is "why is that above this". Each hit
   returns which criteria it met and what each contributed.
   ===================================================================================== */

export type FieldType = 'number' | 'text' | 'enum' | 'boolean' | 'array' | 'date';

export interface SearchField<T = Record<string, unknown>> {
  key: string;
  label: string;
  type: FieldType;
  /** Pulls the comparable value off a row. Kept as a function so a field can be derived. */
  get: (row: T) => unknown;
  /** For enum and array fields, the values worth offering. */
  options?: { value: string; label: string }[];
  /** Shown under the field in the builder, where the meaning is not obvious from the name. */
  hint?: string;
  /** Sensible band for a numeric field, used to seed the range inputs. */
  suggest?: [number, number];
  unit?: string;
}

export type Op =
  | 'is' | 'not' | 'in'            // enum, text
  | 'contains'                     // text
  | 'between' | 'gte' | 'lte'      // number, date
  | 'has' | 'lacks'                // array membership
  | 'present' | 'absent';          // anything nullable

export interface Criterion {
  field: string;
  op: Op;
  value?: unknown;
  /** MUST excludes, PREFER ranks. */
  mode: 'must' | 'prefer';
  /** Relative pull of a PREFER criterion. Ignored for MUST. */
  weight?: number;
}

export interface Reason { field: string; label: string; met: boolean; contribution: number; note?: string }

export interface Hit<T> {
  row: T;
  /** 0 to 100, relative to the best possible score for the criteria given. */
  score: number;
  reasons: Reason[];
}

/* ------------------------------------------------------------------ comparison */

const asNumber = (v: unknown): number | null => {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
};

const asArray = (v: unknown): string[] =>
  Array.isArray(v) ? v.map((x) => String(x)) : v == null ? [] : [String(v)];

const fold = (s: unknown) =>
  String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/* How well a value sits inside a numeric band, from 1 inside to 0 far outside.

   The decay length is the band's own width, so "50 to 200" treats 350 as clearly outside while
   "1000 to 5000" is not upset by 5200. A fixed tolerance would be wrong at one end or the other
   of a range that spans three orders of magnitude, which site volumes do. */
function bandScore(v: number, lo: number, hi: number): number {
  if (v >= lo && v <= hi) return 1;
  const width = Math.max(hi - lo, 1);
  const d = v < lo ? lo - v : v - hi;
  return Math.max(0, 1 - d / width);
}

/* A criterion the user has not finished. An empty value is not a filter for nothing, it is a
   filter that has not been written yet, and both of the plausible readings are wrong:

     as a MUST it excluded nothing, because "contains ''" is true of every string;
     as a PREFER it scored 1 against every row whose field was also empty, so an untouched
     "has a long-read sequencer is [choose]" gave a perfect 100 to every centre we know nothing
     about, which is the exact inverse of the truth.

   So an incomplete criterion does nothing at all, and the builder marks it. */
export function isComplete(c: Criterion): boolean {
  if (c.op === 'present' || c.op === 'absent') return true;
  if (c.op === 'between') {
    const v = c.value as [unknown, unknown] | undefined;
    return Array.isArray(v) && v.length === 2
      && asNumber(v[0]) != null && asNumber(v[1]) != null;
  }
  if (Array.isArray(c.value)) return c.value.length > 0;
  return c.value != null && String(c.value).trim() !== '';
}

/* STRICT satisfaction, for MUST only.

   THE BUG THIS FIXES. MUST used to be tested with degree() > 0, and degree() is deliberately
   GRADED for numeric operators so that a preference can rank near misses. A band of 100 to 200
   decays over its own width, so 236 scored 0.64, which is greater than zero, so it passed. Duke
   University Hospital, at 236 kidney transplants, was returned by a MUST of between 100 and 200,
   and every centre up to 299 passed with it: the requirement was really "between 1 and 300".

   A requirement and a preference cannot share a test. Ranking wants degree. Excluding wants
   this. */
export function satisfies(c: Criterion, raw: unknown): boolean {
  switch (c.op) {
    case 'between': {
      const n = asNumber(raw);
      const v = (c.value as [unknown, unknown]) ?? [];
      const lo = asNumber(v[0]), hi = asNumber(v[1]);
      if (n == null || lo == null || hi == null) return false;
      return n >= Math.min(lo, hi) && n <= Math.max(lo, hi);
    }
    case 'gte': { const n = asNumber(raw), t = asNumber(c.value);
                  return n != null && t != null && n >= t; }
    case 'lte': { const n = asNumber(raw), t = asNumber(c.value);
                  return n != null && t != null && n <= t; }
    // Every other operator is already binary, so the graded path and this one agree.
    default: return degree(c, raw) > 0;
  }
}

/** Degree to which one criterion is satisfied: 0 to 1. Used for RANKING, never for excluding. */
export function degree(c: Criterion, raw: unknown): number {
  const v = raw;

  switch (c.op) {
    case 'present': return v == null || v === '' || (Array.isArray(v) && !v.length) ? 0 : 1;
    case 'absent':  return v == null || v === '' || (Array.isArray(v) && !v.length) ? 1 : 0;

    case 'is':      return fold(v) === fold(c.value) ? 1 : 0;
    case 'not':     return fold(v) === fold(c.value) ? 0 : 1;
    case 'in':      return asArray(c.value).some((x) => fold(x) === fold(v)) ? 1 : 0;
    case 'contains': return fold(v).includes(fold(c.value)) ? 1 : 0;

    case 'has':   return asArray(v).some((x) => fold(x) === fold(c.value)
                          || fold(x).includes(fold(c.value))) ? 1 : 0;
    case 'lacks': return asArray(v).some((x) => fold(x) === fold(c.value)
                          || fold(x).includes(fold(c.value))) ? 0 : 1;

    case 'gte': { const n = asNumber(v), t = asNumber(c.value);
                  if (n == null || t == null) return 0;
                  return n >= t ? 1 : Math.max(0, 1 - (t - n) / Math.max(t, 1)); }
    case 'lte': { const n = asNumber(v), t = asNumber(c.value);
                  if (n == null || t == null) return 0;
                  return n <= t ? 1 : Math.max(0, 1 - (n - t) / Math.max(t, 1)); }
    case 'between': {
      const n = asNumber(v);
      const [lo, hi] = (c.value as [number, number]) ?? [0, 0];
      if (n == null) return 0;
      return bandScore(n, Number(lo), Number(hi));
    }
    default: return 0;
  }
}

/* ------------------------------------------------------------------ the search */

export interface SearchOptions {
  limit?: number;
  /** Applied to every field of type text before the criteria, for a plain keyword box. */
  text?: string;
  textFields?: string[];
  /** Ranks by this field when no PREFER criterion is set, so a bare filter is still ordered. */
  fallbackSort?: { field: string; direction: 'asc' | 'desc' };
}

export function runSearch<T extends Record<string, unknown>>(
  rows: T[],
  fields: SearchField<T>[],
  criteria: Criterion[],
  opts: SearchOptions = {},
): { hits: Hit<T>[]; total: number; excludedBy: Record<string, number> } {
  const byKey = new Map(fields.map((f) => [f.key, f]));
  /* Unfinished criteria are dropped rather than evaluated. See isComplete. */
  const usable = criteria.filter((c) => byKey.has(c.field) && isComplete(c));
  const musts = usable.filter((c) => c.mode === 'must');
  const prefers = usable.filter((c) => c.mode === 'prefer');

  /* Which MUST threw out how much. A search returning nothing is otherwise a dead end: this
     lets the interface say "country excluded 1,180 of them" instead of "no results". */
  const excludedBy: Record<string, number> = {};

  const text = (opts.text ?? '').trim();
  const textKeys = opts.textFields ?? fields.filter((f) => f.type === 'text').map((f) => f.key);

  const kept: Hit<T>[] = [];
  for (const row of rows) {
    if (text) {
      const hay = textKeys.map((k) => fold(byKey.get(k)?.get(row))).join(' ');
      if (!fold(text).split(/\s+/).every((w) => hay.includes(w))) continue;
    }

    let excluded = false;
    for (const c of musts) {
      if (!satisfies(c, byKey.get(c.field)!.get(row))) {
        excludedBy[c.field] = (excludedBy[c.field] ?? 0) + 1;
        excluded = true;
        break;
      }
    }
    if (excluded) continue;

    const reasons: Reason[] = [];
    let got = 0, possible = 0;
    for (const c of prefers) {
      const f = byKey.get(c.field)!;
      const w = c.weight ?? 1;
      const d = degree(c, f.get(row));
      possible += w;
      got += w * d;
      reasons.push({
        field: c.field, label: f.label, met: d > 0,
        contribution: Math.round(w * d * 100) / 100,
        note: d > 0 && d < 1 ? 'partial' : undefined,
      });
    }
    for (const c of musts) {
      const f = byKey.get(c.field)!;
      reasons.push({ field: c.field, label: f.label, met: true, contribution: 0, note: 'required' });
    }

    kept.push({ row, score: possible > 0 ? Math.round((got / possible) * 1000) / 10 : 100, reasons });
  }

  /* Ranking. Score first; then the fallback, so a search with only MUST criteria still comes
     back in a useful order rather than whatever order the seed happened to be written in. */
  const fb = opts.fallbackSort;
  const fbField = fb ? byKey.get(fb.field) : undefined;
  kept.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    if (!fbField) return 0;
    const av = asNumber(fbField.get(a.row)) ?? -Infinity;
    const bv = asNumber(fbField.get(b.row)) ?? -Infinity;
    return fb!.direction === 'desc' ? bv - av : av - bv;
  });

  const total = kept.length;
  return { hits: opts.limit ? kept.slice(0, opts.limit) : kept, total, excludedBy };
}

/** Operators that make sense for a field, so the builder never offers a nonsensical pair. */
export function opsFor(type: FieldType): { op: Op; label: string }[] {
  switch (type) {
    case 'number':
      return [{ op: 'between', label: 'between' }, { op: 'gte', label: 'at least' },
              { op: 'lte', label: 'at most' }, { op: 'present', label: 'is recorded' },
              { op: 'absent', label: 'is not recorded' }];
    case 'enum':
      return [{ op: 'is', label: 'is' }, { op: 'not', label: 'is not' },
              { op: 'in', label: 'is any of' }];
    case 'array':
      return [{ op: 'has', label: 'includes' }, { op: 'lacks', label: 'does not include' },
              { op: 'present', label: 'has any' }, { op: 'absent', label: 'has none' }];
    case 'boolean':
      return [{ op: 'is', label: 'is' }];
    case 'date':
      return [{ op: 'gte', label: 'on or after' }, { op: 'lte', label: 'on or before' },
              { op: 'present', label: 'is set' }, { op: 'absent', label: 'is not set' }];
    default:
      return [{ op: 'contains', label: 'contains' }, { op: 'is', label: 'is exactly' },
              { op: 'present', label: 'is recorded' }, { op: 'absent', label: 'is not recorded' }];
  }
}
